import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../ui/button";
import { Play, Square, X } from "lucide-react";

/**
 * 4-7-8 visual breathing guide. A pulsing circle syncs the user's breath:
 * inhale 4s → hold 7s → exhale 8s. Purely visual (no audio).
 */
const PHASES = [
  { key: "inhale", duration: 4000, scale: 1.35, color: "#06b6d4" },
  { key: "hold",   duration: 7000, scale: 1.35, color: "#8b5cf6" },
  { key: "exhale", duration: 8000, scale: 0.75, color: "#10b981" },
];

const BreathingGuide = ({ onClose }) => {
  const { t } = useLanguage();
  const [active, setActive] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cyclesDone, setCyclesDone] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const phase = PHASES[phaseIdx];
    timerRef.current = setTimeout(() => {
      const nextIdx = (phaseIdx + 1) % PHASES.length;
      if (nextIdx === 0) setCyclesDone((c) => c + 1);
      setPhaseIdx(nextIdx);
    }, phase.duration);
    return () => clearTimeout(timerRef.current);
  }, [active, phaseIdx]);

  const start = () => { setActive(true); setPhaseIdx(0); setCyclesDone(0); };
  const stop = () => { setActive(false); clearTimeout(timerRef.current); };

  const phase = PHASES[phaseIdx];
  const countdown = Math.ceil(phase.duration / 1000);

  return (
    <div className="bg-[#0a0a0f] rounded-3xl p-8 border border-white/10" data-testid="breathing-guide">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-['Outfit'] text-2xl font-semibold mb-1">{t("tool_breathing_title")}</h2>
          <p className="text-white/50 text-sm">{t("tool_breathing_subtitle")}</p>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white p-2" data-testid="breathing-close-btn" aria-label="close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col items-center justify-center py-10 min-h-[360px]">
        <div
          className="w-48 h-48 rounded-full flex items-center justify-center"
          style={{
            transform: `scale(${active ? phase.scale : 1})`,
            transition: `transform ${active ? phase.duration : 400}ms ease-in-out, background-color 600ms`,
            background: `radial-gradient(circle, ${active ? phase.color : "#64748b"}40, ${active ? phase.color : "#64748b"}10)`,
            border: `2px solid ${active ? phase.color : "#64748b"}80`,
            boxShadow: active ? `0 0 60px ${phase.color}60` : "none",
          }}
        >
          <div className="text-center">
            <p className="text-white/80 text-sm uppercase tracking-widest mb-1" data-testid="breathing-phase">
              {active ? t(`tool_breathing_${phase.key}`) : t("tool_breathing_ready")}
            </p>
            {active && <p className="text-white text-3xl font-['Outfit'] font-semibold" data-testid="breathing-countdown">{countdown}</p>}
          </div>
        </div>
        <p className="text-white/40 text-xs mt-6 text-center max-w-xs">{t("tool_breathing_hint")}</p>
        {cyclesDone > 0 && <p className="text-cyan-300 text-sm mt-3" data-testid="breathing-cycles">{cyclesDone} {t("tool_breathing_cycles")}</p>}
      </div>

      <div className="flex justify-center gap-3">
        {!active ? (
          <Button onClick={start} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90 text-white rounded-full px-8 py-5" data-testid="breathing-start-btn">
            <Play className="w-4 h-4 mr-2" /> {t("tool_breathing_start")}
          </Button>
        ) : (
          <Button onClick={stop} variant="ghost" className="border border-white/20 text-white rounded-full px-8 py-5" data-testid="breathing-stop-btn">
            <Square className="w-4 h-4 mr-2" /> {t("tool_breathing_stop")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BreathingGuide;
